import { useEffect, useMemo, useState } from "react";
import { CreditCard, Download, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Admin panel - Statystyki generatora tablic.
 *
 * Displays two headline counters (total plates rendered vs. total PNGs
 * downloaded) plus supporting breakdowns:
 *   · 7-day and 30-day activity slices
 *   · Downloads by resolution (SD / HD / 2K / 4K)
 *   · Top 10 wyróżniki (most-used prefixes)
 *   · 30-day daily timeline (simple SVG bar chart, no chart deps)
 *   · Latest 20 events
 */
export default function PlateStatsManager() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/admin/stats/plates");
            setData(r.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading && !data) {
        return (
            <div className="flex items-center gap-2 text-[color:var(--text-secondary)] text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Wczytywanie statystyk…
            </div>
        );
    }
    if (err) {
        return (
            <div className="panel-yellow p-6 text-sm text-red-400">
                {err}
                <button
                    type="button"
                    onClick={load}
                    className="ml-3 underline"
                >
                    Spróbuj ponownie
                </button>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="admin-plate-stats">
            {/* Headline counters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StatCard
                    icon={<Sparkles className="w-4 h-4" />}
                    label="Wygenerowane tablice"
                    value={data.totals.generated}
                    hint={`7 dni: ${data.last_7d.generated} · 30 dni: ${data.last_30d.generated}`}
                    testid="plate-stats-generated"
                />
                <StatCard
                    icon={<Download className="w-4 h-4" />}
                    label="Pobrane tablice"
                    value={data.totals.downloaded}
                    hint={`7 dni: ${data.last_7d.downloaded} · 30 dni: ${data.last_30d.downloaded}`}
                    testid="plate-stats-downloaded"
                />
            </div>

            {/* Actions row */}
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">
                    Statystyki generatora tablic
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)]"
                >
                    <RefreshCw className="w-3 h-3" />
                    Odśwież
                </button>
            </div>

            {/* By-scale + Top prefixes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="panel-yellow p-5">
                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                        <Download className="w-3 h-3" />
                        Pobrania wg rozdzielczości
                    </div>
                    {data.by_scale.length === 0 ? (
                        <p className="text-xs text-[color:var(--text-muted)]">
                            Brak pobrań w bazie.
                        </p>
                    ) : (
                        <ul className="space-y-1.5 text-sm">
                            {data.by_scale.map((r) => (
                                <li
                                    key={r.scale}
                                    className="flex items-center justify-between"
                                >
                                    <span className="text-[color:var(--text-secondary)]">
                                        {scaleLabel(r.scale)}
                                    </span>
                                    <span className="tabular-nums font-black text-[color:var(--text-primary)]">
                                        {r.count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="panel-yellow p-5">
                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                        <CreditCard className="w-3 h-3" />
                        Najpopularniejsze wyróżniki
                    </div>
                    {data.top_prefixes.length === 0 ? (
                        <p className="text-xs text-[color:var(--text-muted)]">
                            Brak danych.
                        </p>
                    ) : (
                        <ul className="space-y-1.5 text-sm">
                            {data.top_prefixes.map((r) => (
                                <li
                                    key={r.prefix}
                                    className="flex items-center justify-between"
                                >
                                    <span className="font-black tracking-widest text-[color:var(--text-primary)]">
                                        {r.prefix}
                                    </span>
                                    <span className="tabular-nums text-[color:var(--text-secondary)]">
                                        {r.count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* By-variant breakdown */}
            <div className="panel-yellow p-5" data-testid="plate-stats-by-variant">
                <div className="label-eyebrow mb-3 flex items-center gap-2">
                    <CreditCard className="w-3 h-3" />
                    Typ tablicy
                </div>
                {(!data.by_variant || data.by_variant.length === 0) ? (
                    <p className="text-xs text-[color:var(--text-muted)]">
                        Brak danych.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                                    <th className="py-2 pr-3">Wariant</th>
                                    <th className="py-2 pr-3 text-right">
                                        Wygenerowane
                                    </th>
                                    <th className="py-2 pr-3 text-right">
                                        Pobrane
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_variant.map((r) => (
                                    <tr
                                        key={r.variant}
                                        className="border-t border-white/5"
                                        data-testid={`plate-variant-row-${r.variant}`}
                                    >
                                        <td className="py-2 pr-3 font-black uppercase tracking-[0.2em] text-[color:var(--text-primary)]">
                                            {variantLabel(r.variant)}
                                        </td>
                                        <td className="py-2 pr-3 text-right tabular-nums text-[color:var(--primary)]">
                                            {r.generated}
                                        </td>
                                        <td className="py-2 pr-3 text-right tabular-nums text-emerald-300">
                                            {r.downloaded}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Timeline */}
            <div className="panel-yellow p-5">
                <div className="label-eyebrow mb-3">Ostatnie 30 dni</div>
                <Timeline rows={data.timeline} />
                <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-[color:var(--primary)]" />
                        Wygenerowane
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" />
                        Pobrane
                    </span>
                </div>
            </div>

            {/* Recent */}
            <div className="panel-yellow p-5">
                <div className="label-eyebrow mb-3">Ostatnie zdarzenia</div>
                {data.recent.length === 0 ? (
                    <p className="text-xs text-[color:var(--text-muted)]">
                        Brak zdarzeń.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                                    <th className="py-2 pr-3">Kiedy</th>
                                    <th className="py-2 pr-3">Rodzaj</th>
                                    <th className="py-2 pr-3">Typ</th>
                                    <th className="py-2 pr-3">Tablica</th>
                                    <th className="py-2 pr-3">Rozmiar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="border-t border-white/5"
                                    >
                                        <td className="py-2 pr-3 tabular-nums text-[color:var(--text-secondary)]">
                                            {formatDate(r.created_at)}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <span
                                                className={
                                                    r.kind === "downloaded"
                                                        ? "text-emerald-300"
                                                        : "text-[color:var(--primary)]"
                                                }
                                            >
                                                {r.kind === "downloaded"
                                                    ? "Pobranie"
                                                    : "Generacja"}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-3 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                                            {variantLabel(r.variant)}
                                        </td>
                                        <td className="py-2 pr-3 font-black tracking-widest text-[color:var(--text-primary)]">
                                            {r.plate || `${r.prefix}${r.tail}`}
                                        </td>
                                        <td className="py-2 pr-3 tabular-nums text-[color:var(--text-secondary)]">
                                            {r.w && r.h
                                                ? `${r.w}×${r.h}`
                                                : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- helpers ---------------------------------------------------------------

function StatCard({ icon, label, value, hint, testid }) {
    return (
        <div className="panel-yellow p-5" data-testid={testid}>
            <div className="flex items-center gap-2 label-eyebrow mb-2">
                {icon}
                {label}
            </div>
            <div className="text-4xl font-black text-[color:var(--text-primary)] tabular-nums">
                {value.toLocaleString("pl-PL")}
            </div>
            <div className="text-[11px] text-[color:var(--text-muted)] mt-1">
                {hint}
            </div>
        </div>
    );
}

function scaleLabel(s) {
    if (s === 1) return "SD (1×) - 1924×422";
    if (s === 2) return "HD (2×) - 3848×844";
    if (s === 4) return "2K (4×) - 7696×1688";
    if (s === 8) return "4K (8×) - 15392×3376";
    return `Skala ${s}×`;
}

function variantLabel(v) {
    if (v === "zabytkowa") return "Zabytkowa";
    if (v === "standard") return "Standardowa";
    return v || "Standardowa";
}

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("pl-PL", {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function Timeline({ rows }) {
    const max = useMemo(() => {
        let m = 1;
        for (const r of rows) m = Math.max(m, r.generated, r.downloaded);
        return m;
    }, [rows]);
    const barW = 100 / rows.length;
    const height = 120;
    return (
        <svg
            viewBox={`0 0 100 ${height}`}
            preserveAspectRatio="none"
            className="w-full h-32"
        >
            {rows.map((r, i) => {
                const x = i * barW;
                const genH = (r.generated / max) * (height - 6);
                const dlH = (r.downloaded / max) * (height - 6);
                return (
                    <g key={r.day} transform={`translate(${x}, 0)`}>
                        {/* Generated bar - yellow */}
                        <rect
                            x={0.3}
                            y={height - 3 - genH}
                            width={barW / 2 - 0.4}
                            height={genH}
                            fill="var(--primary)"
                            opacity={0.9}
                        >
                            <title>
                                {r.day}: {r.generated} wygenerowanych
                            </title>
                        </rect>
                        {/* Downloaded bar - green */}
                        <rect
                            x={barW / 2 + 0.1}
                            y={height - 3 - dlH}
                            width={barW / 2 - 0.4}
                            height={dlH}
                            fill="#34d399"
                            opacity={0.9}
                        >
                            <title>
                                {r.day}: {r.downloaded} pobrań
                            </title>
                        </rect>
                    </g>
                );
            })}
            <line
                x1={0}
                y1={height - 2}
                x2={100}
                y2={height - 2}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={0.5}
            />
        </svg>
    );
}
